import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Feedback, type FeedbackSource, type SentimentLabel, type FeedbackCategory } from '../lib/api';
import { SentimentBadge } from '../components/SentimentBadge';

const SOURCES: FeedbackSource[] = ['zendesk', 'discord', 'github', 'email', 'twitter', 'forum'];
const SENTIMENTS: SentimentLabel[] = ['positive', 'negative', 'neutral', 'mixed'];
const CATEGORIES: FeedbackCategory[] = [
    'bug', 'feature-request', 'ux-issue', 'performance',
    'pricing', 'documentation', 'integration', 'support'
];

export const FeedbackExplorer: React.FC = () => {
    const navigate = useNavigate();
    const [feedback, setFeedback] = useState<Feedback[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        source: '',
        sentiment: '',
        category: '',
        days: 30,
        search: ''
    });
    const [page, setPage] = useState(0);
    const limit = 20;

    useEffect(() => {
        loadFeedback();
    }, [filters, page]);

    const loadFeedback = async () => {
        setLoading(true);
        try {
            const params: any = {
                limit,
                offset: page * limit,
                days: filters.days
            };

            if (filters.source) params.source = filters.source;
            if (filters.sentiment) params.sentiment = filters.sentiment;
            if (filters.category) params.category = filters.category;
            if (filters.search) params.search = filters.search;

            const result = await api.getFeedback(params);
            setFeedback(result.items);
            setTotal(result.total);
        } catch (error) {
            console.error('Failed to load feedback:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getSourceIcon = (source: FeedbackSource) => {
        const icons: Record<FeedbackSource, string> = {
            zendesk: '🎫',
            discord: '💬',
            github: '🐙',
            email: '📧',
            twitter: '🐦',
            forum: '💭'
        };
        return icons[source];
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gradient">Feedback Explorer</h1>
                <p className="text-gray-600 mt-1">Search and filter all feedback across sources</p>
            </div>

            {/* Filter Bar */}
            <div className="card">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* Search */}
                    <div className="lg:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                        <input
                            type="text"
                            className="input"
                            placeholder="Search in title and content..."
                            value={filters.search}
                            onChange={(e) => {
                                setFilters({ ...filters, search: e.target.value });
                                setPage(0);
                            }}
                        />
                    </div>

                    {/* Source */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                        <select
                            className="input"
                            value={filters.source}
                            onChange={(e) => {
                                setFilters({ ...filters, source: e.target.value });
                                setPage(0);
                            }}
                        >
                            <option value="">All Sources</option>
                            {SOURCES.map(source => (
                                <option key={source} value={source}>{getSourceIcon(source)} {source}</option>
                            ))}
                        </select>
                    </div>

                    {/* Sentiment */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sentiment</label>
                        <select
                            className="input"
                            value={filters.sentiment}
                            onChange={(e) => {
                                setFilters({ ...filters, sentiment: e.target.value });
                                setPage(0);
                            }}
                        >
                            <option value="">All Sentiments</option>
                            {SENTIMENTS.map(sentiment => (
                                <option key={sentiment} value={sentiment}>{sentiment}</option>
                            ))}
                        </select>
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <select
                            className="input"
                            value={filters.category}
                            onChange={(e) => {
                                setFilters({ ...filters, category: e.target.value });
                                setPage(0);
                            }}
                        >
                            <option value="">All Categories</option>
                            {CATEGORIES.map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-4 flex flex-wrap gap-2">
                    <button
                        onClick={() => {
                            setFilters({ ...filters, category: 'bug', sentiment: 'negative', days: 7 });
                            setPage(0);
                        }}
                        className="btn-secondary text-sm"
                    >
                        🐛 Critical Bugs
                    </button>
                    <button
                        onClick={() => {
                            setFilters({ ...filters, category: 'feature-request', days: 30 });
                            setPage(0);
                        }}
                        className="btn-secondary text-sm"
                    >
                        💡 Feature Requests
                    </button>
                    <button
                        onClick={() => {
                            setFilters({ ...filters, sentiment: 'negative', days: 7 });
                            setPage(0);
                        }}
                        className="btn-secondary text-sm"
                    >
                        😞 Recent Negative
                    </button>
                    <button
                        onClick={() => {
                            setFilters({ source: '', sentiment: '', category: '', days: 30, search: '' });
                            setPage(0);
                        }}
                        className="btn-secondary text-sm"
                    >
                        ↺ Reset
                    </button>
                </div>
            </div>

            {/* Results */}
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-sm text-gray-600">
                        Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total} results
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-pulse text-gray-500">Loading feedback...</div>
                    </div>
                ) : feedback.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        No feedback found. Try adjusting your filters.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {feedback.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => navigate(`/feedback/${item.id}`)}
                                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-md transition-all cursor-pointer"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-lg">{getSourceIcon(item.source)}</span>
                                            <span className="text-xs font-medium text-gray-500 uppercase">{item.source}</span>
                                            <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
                                            {item.analysis && (
                                                <>
                                                    <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-medium capitalize">
                                                        {item.analysis.category.replace('-', ' ')}
                                                    </span>
                                                    {item.analysis.urgency_score >= 4 && (
                                                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                                                            ⚠️ Urgent
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {item.title && (
                                            <h3 className="font-semibold text-gray-900 mb-1">{item.title}</h3>
                                        )}

                                        <p className="text-sm text-gray-600 line-clamp-2">{item.content}</p>

                                        {item.author && (
                                            <div className="mt-2 text-xs text-gray-500">
                                                By {item.author}
                                                {item.author_segment && (
                                                    <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded">
                                                        {item.author_segment}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {item.analysis && (
                                        <div className="flex flex-col items-end gap-2">
                                            <SentimentBadge
                                                sentiment={item.analysis.sentiment_label}
                                                score={item.analysis.sentiment_score}
                                            />
                                            <div className="flex gap-1">
                                                <span className="text-xs text-gray-500">
                                                    U: {item.analysis.urgency_score}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    V: {item.analysis.value_score}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between">
                        <button
                            onClick={() => setPage(Math.max(0, page - 1))}
                            disabled={page === 0}
                            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <div className="text-sm text-gray-600">
                            Page {page + 1} of {totalPages}
                        </div>
                        <button
                            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                            disabled={page >= totalPages - 1}
                            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
